import React, { useMemo, useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from './useTheme';
import GridContainer from './GridContainer';
import { ApiDataLoader } from './ApiDataLoader';
import { getRegisteredComponent } from './ComponentRegistry';
import { REQUIRED_SYSTEM_MODULES } from './types';
import type { GridLayoutConfig, LayoutItem } from '../Config/config';
import { LazyLoadLogger } from '../Services/LazyLoadLogger';
import { AdaptivePreloadService } from '../Services/AdaptivePreloadService';
import { MediaImportanceService } from '../Services/MediaImportanceService';
import { LoadingQueueService } from '../Services/LoadingQueueService';
import { PagePreloadOrchestrator } from '../Services/PagePreloadOrchestrator';
import { usePagePreload } from '../Hooks/usePagePreload';
import { useModulesApi } from '../Hooks/useModulesApi';

/**
 * PageManager - Gère les configurations par page
 * 
 * Logique de priorité :
 * 1. Si la page a une grille personnalisée (page.gridLayout) : utiliser cette grille
 * 2. Sinon, filtrer la grille globale selon les règles de page :
 *    - Si item.page est défini : afficher uniquement sur cette page
 *    - Si item.page n'est pas défini : afficher sur toutes les pages
 */
export const PageManager: React.FC = () => {
    const { config, actions } = useTheme();
    const location = useLocation();
    const { preloadPage } = usePagePreload();
    const { loadInstances } = useModulesApi(config.api);
    const [apiPageState, setApiPageState] = useState<Record<string, 'loading' | 'loaded' | 'error'>>({});
    
    const handleApiPageStateChange = useCallback((pageUrl: string, state: 'loading' | 'loaded' | 'error') => {
        setApiPageState(prev => ({ ...prev, [pageUrl || '/']: state }));
    }, []);
    
    // Trouver la page correspondant à l'URL actuelle
    const currentPage = config.PagesList.find(page => page.pageUrl === location.pathname);
    
    // Initialiser le système de lazy loading
    useEffect(() => {
        // Initialiser le logger : priorité config.logs.enabled puis overlay
        const loggingEnabled = (config as any).logs?.enabled ?? (config.gridLayout.overlay?.enabled || false);
        LazyLoadLogger.init(loggingEnabled);
        
        // Configurer les services si lazy loading activé
        const lazyConfig = config.api.media?.lazyLoading;
        if (lazyConfig?.enabled) {
            LazyLoadLogger.logPreload('Initialisation du système de lazy loading');
            
            // Configurer AdaptivePreloadService
            AdaptivePreloadService.configure({
                baseDistance: lazyConfig.basePreloadDistance,
                scrollSpeedThreshold: 1000,
                maxDistance: lazyConfig.basePreloadDistance * 4,
                minDistance: 100
            });
            
            // Configurer MediaImportanceService
            MediaImportanceService.configure({
                weights: lazyConfig.importanceWeights,
                videoBonus: 1.2
            });
            
            // Configurer LoadingQueueService
            LoadingQueueService.configure(
                lazyConfig.maxParallelLoads,
                lazyConfig.pauseThreshold
            );
            
            // Démarrer la surveillance de charge
            LoadingQueueService.startLoadMonitoring();
        }
        
        return () => {
            // Cleanup seulement si le lazy loading est désactivé
            if (!lazyConfig?.enabled) {
                LoadingQueueService.stopLoadMonitoring();
                PagePreloadOrchestrator.stopAutoLoadMonitoring();
            }
        };
    }, [config.gridLayout.overlay?.enabled, config.api.media?.lazyLoading]);
    
    // Précharger les pages accessibles
    useEffect(() => {
        const lazyConfig = config.api.media?.lazyLoading;
        
        LazyLoadLogger.logPreload('🔧 Vérification config pour préchargement pages', {
            hasApi: !!config.api,
            hasMedia: !!config.api?.media,
            hasLazyLoading: !!config.api?.media?.lazyLoading,
            enabled: lazyConfig?.enabled,
            config: lazyConfig
        });
        
        if (!lazyConfig?.enabled) {
            LazyLoadLogger.warn('preload', '⚠️ Préchargement pages désactivé (lazyLoading.enabled = false)');
            return;
        }
        
        // Attendre après la deadline de la page actuelle (3s) + 500ms de buffer
        // ET attendre que le DOM soit complètement rendu (important pour scanner les liens)
        const timeout = setTimeout(() => {
            // Double vérification que le DOM est bien prêt
            requestAnimationFrame(() => {
                LazyLoadLogger.logPreload(`🌐 Scan DOM pour pages accessibles depuis: ${location.pathname}`);
                LazyLoadLogger.logPreload(`📊 Nombre de liens dans le DOM: ${document.querySelectorAll('a').length}`);
                LazyLoadLogger.logPreload(`📊 Config PagesList: ${config.PagesList.length} pages`);
                
                PagePreloadOrchestrator.startPreloading(
                    location.pathname,
                    config,
                    async (url: string) => {
                        // 🆕 Charger TOUTES les instances, puis filtrer pour la page spécifique
                        LazyLoadLogger.logPreload(`🔄 Appel loadInstances pour: ${url}`);
                        try {
                            // Charger toutes les instances (sans paramètre)
                            const allInstances = await loadInstances();
                            LazyLoadLogger.logPreload(`📦 Toutes les instances reçues:`, {
                                count: allInstances.length,
                                composants: allInstances.map(i => i.component)
                            });
                            
                            // Filtrer pour la page spécifique
                            const pageInstances = allInstances.filter(instance => instance.page === url);
                            LazyLoadLogger.logPreload(`🔍 Instances filtrées pour ${url}:`, {
                                count: pageInstances.length,
                                composants: pageInstances.map(i => i.component)
                            });
                            
                            return pageInstances;
                        } catch (error) {
                            LazyLoadLogger.error('preload', `❌ Erreur loadInstances pour ${url}`, error);
                            return [];
                        }
                    },
                    () => {
                        LazyLoadLogger.logPreload('✅ Tous les viewports des pages accessibles préchargés');
                    }
                );
                
                // Démarrer la surveillance automatique
                PagePreloadOrchestrator.startAutoLoadMonitoring();
            });
        }, 2000); // Réduire le délai à 2s pour un préchargement plus rapide
        
        return () => {
            clearTimeout(timeout);
            // Ne pas réinitialiser l'orchestrateur pour éviter d'annuler le préchargement en cours
        };
    }, [location.pathname, config, loadInstances, config.api.media?.lazyLoading]);
    
    // Déterminer quelle grille utiliser pour cette page
    const pageGridLayout: GridLayoutConfig = useMemo(() => {
        // Si la page a sa propre grille, l'utiliser
        if (currentPage?.gridLayout) {
            console.log(`📄 Page "${currentPage.pageName}" utilise sa grille personnalisée avec ${currentPage.gridLayout.items.length} composants`);
            return currentPage.gridLayout;
        }
        
        // Sinon, filtrer la grille globale pour cette page
        const pageSpecificItems = config.gridLayout.items.filter((item: LayoutItem) => {
            // Si l'item a une page définie, afficher seulement sur cette page
            if (item.page) {
                return item.page === location.pathname;
            }
            // Sinon, afficher sur toutes les pages
            return true;
        });
        
        console.log(`📄 Page "${currentPage?.pageName || 'par défaut'}" utilise la grille globale filtrée avec ${pageSpecificItems.length} composants`);
        
        return {
            ...config.gridLayout,
            items: pageSpecificItems
        };
    }, [config.gridLayout, location.pathname, currentPage]);
    
    // Rendre les modules système (toujours en outsideGrid)
    const systemModulesComponents = useMemo(() => {
        return REQUIRED_SYSTEM_MODULES.map(moduleName => {
            const Component = getRegisteredComponent(moduleName);
            if (!Component) {
                return null;
            }
            return <Component key={`system-${moduleName}`} />;
        }).filter(Boolean);
    }, []);

    return (
        <>
            {/* Chargeur automatique des données depuis l'API */}
            <ApiDataLoader 
                config={config}
                onConfigChange={actions.updateConfig}
                currentPageUrl={location.pathname}
                onPageLoadStateChange={handleApiPageStateChange}
            />
            
            {/* Modules système (toujours rendus en premier) */}
            {systemModulesComponents}
            
            {/* Grille principale */}
            <GridContainer
                layout={pageGridLayout}
                apiLoading={apiPageState[location.pathname || '/'] === 'loading'}
            />
        </>
    );
};

export default PageManager;

